import React, { useState, useEffect, useRef } from "react";
import "./SpinnerTextBox.css";

const FUNC_KEYS = "F1F2F3F4F5F6F7F8F9F10F11F12";

type Props = {
  style?: React.CSSProperties;
  value: number;
  onValueChanged: (value: number) => void;
  disabled?: boolean;
  step?: number;
  min: number;
  max: number;
  className?: string;
  onErrorOccured?: () => void;
  onErrorCleared?: () => void;
};

export default function SpinnerTextBox({
  style = {},
  value,
  onValueChanged,
  disabled = false,
  step = 1,
  min,
  max,
  className,
  onErrorOccured = () => {},
  onErrorCleared = () => {},
}: Props) {
  const [isError, setIsError] = useState(false);
  const [currentValue, setCurrentValue] = useState("");
  const textBoxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentValue(value.toFixed(0));
  }, [value]);

  const onValueUpdated = (value: string) => {
    if (disabled) {
      return;
    }
    let numberValue = parseFloat(value);
    if (isNaN(numberValue)) {
      setIsError(true);
      onErrorOccured();
      return;
    }

    let clippedValue = numberValue;
    clippedValue = Math.min(clippedValue, max);
    clippedValue = Math.max(clippedValue, min);
    onValueChanged(clippedValue);
    setCurrentValue(clippedValue.toFixed(0));
    setIsError(false);
    onErrorCleared();
  };

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      textBoxRef.current?.blur();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const selectionStart = textBoxRef.current?.selectionStart ?? 0;
    const selectionEnd = textBoxRef.current?.selectionStart ?? 0;
    const oldText = textBoxRef.current?.value ?? "";
    const fullText =
      oldText.slice(0, selectionStart) +
      e.key +
      oldText.slice(selectionStart, selectionEnd);
    if (
      !FUNC_KEYS.includes(e.key) &&
      e.key !== "ArrowLeft" &&
      e.key !== "ArrowRight" &&
      e.key !== "ArrowUp" &&
      e.key !== "ArrowDown" &&
      e.key !== "Backspace" &&
      e.key !== "Escape" &&
      e.key !== "Enter" &&
      e.key !== "Delete" &&
      e.key !== "Tab" &&
      e.key !== "Home" &&
      e.key !== "End" &&
      e.ctrlKey !== true &&
      !fullText.match(/^[-]?\d*\.?\d*$/)
    )
      e.preventDefault();
  };

  return (
    <div
      className={"spinner-text-box" + " " + className}
      style={{
        border: disabled ? "1px solid #CCCCCC" : "1px solid #4D4D4D",
        ...style,
      }}
    >
      <input
        ref={textBoxRef}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        onBlur={(e) => onValueUpdated(e.target.value)}
        onChange={(e) => {
          setCurrentValue(e.target.value);
        }}
        style={{
          border: disabled ? "1px solid #CCCCCC" : "unset",
          backgroundColor: disabled
            ? "#e0e0e0"
            : isError
            ? "rgb(233, 120, 126)"
            : "transparent",
        }}
        type="number"
        min={min}
        max={max}
        value={currentValue}
        disabled={disabled}
      />
      <div className="input-button">
        <svg
          tabIndex={1}
          onClick={() =>
            onValueUpdated(
              (Number.parseFloat(currentValue || "0") + step).toString()
            )
          }
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          viewBox="0 0 31 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g>
            <path
              d="M30.2325 0.95459H1.23254V14.5H30.2325V0.95459Z"
              fill="#E2E2E2"
            />
            <path
              d="M30.2325 0.95459H1.23254V14.5H30.2325V0.95459Z"
              fill="url(#paint0_linear)"
            />
            <path
              d="M30.2325 0.95459H1.23254V14.5H30.2325V0.95459Z"
              stroke="#BDBDBD"
            />
            <path
              d="M20.7325 10.4546L15.7325 4.45459L10.7325 10.4546H13.8575H14.4825H16.9825H17.6075H20.7325Z"
              fill="#4D4D4D"
            />
          </g>
        </svg>

        <svg
          tabIndex={1}
          onClick={() =>
            onValueUpdated(
              (Number.parseFloat(currentValue || "0") - step).toString()
            )
          }
          width="100%"
          height="100%"
          preserveAspectRatio="none"
          viewBox="0 0 31 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g>
            <path
              d="M30.2325 14.0454H1.23254V0.499954H30.2325V14.0454Z"
              fill="#E2E2E2"
            />
            <path
              d="M30.2325 14.0454H1.23254V0.499954H30.2325V14.0454Z"
              fill="url(#paint0_linear)"
            />
            <path
              d="M30.2325 14.0454H1.23254V0.499954H30.2325V14.0454Z"
              stroke="#BDBDBD"
            />
            <path
              d="M20.7325 4.54541L15.7325 10.5454L10.7325 4.54541H13.8575H14.4825L16.9825 4.54541H17.6075H20.7325Z"
              fill="#4D4D4D"
            />
          </g>
        </svg>
      </div>
    </div>
  );
}
